/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from 'react';
import { useJobTitleContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useJobTitle = () => {
    const [state, dispatch] = useJobTitleContext(); // Updated context hook name

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchJobTitles = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.JOBTITLES, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_JOBTITLES, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const searchJobTitles = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchJobTitles();
        },
        [setFilter, setPage, fetchJobTitles]
    );

    const createJobTitle = useCallback(
        async (jobTitleData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.JOBTITLES, jobTitleData);
                dispatch({ type: ACTIONS.ADD_JOBTITLE, payload: data });

                showSuccessMessage('Job Title created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateJobTitle = useCallback(
        async (id, jobTitleData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.JOBTITLES}/${id}`, jobTitleData);
                dispatch({ type: ACTIONS.UPDATE_JOBTITLE, payload: { id, data } });

                showSuccessMessage('Job Title updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteJobTitle = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.JOBTITLES}/${id}`);
                dispatch({ type: ACTIONS.DELETE_JOBTITLE, payload: id });

                showSuccessMessage('Job Title deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getJobTitleById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.JOBTITLES}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_JOBTITLE, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listJobTitle = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.JOBTITLES, page, size);
                dispatch({ type: ACTIONS.SET_JOBTITLES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            jobTitles: state.jobTitles, // Updated to jobTitles
            selectedJobTitle: state.selectedJobTitle, // Updated to selectedJobTitle
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchJobTitles,
            searchJobTitles,
            listJobTitle,

            // CRUD Operations
            createJobTitle,
            updateJobTitle,
            deleteJobTitle,
            getJobTitleById,

            // Utilities
            clearError
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchJobTitles,
            searchJobTitles,
            createJobTitle,
            updateJobTitle,
            deleteJobTitle,
            getJobTitleById,
            clearError
        ]
    );
};

export default useJobTitle;
